import { ValidationError } from 'yup';
import { USER_INSIGHT_FIELD_TYPES } from '@/admin/api/insightFieldsApi';
import {
	INSIGHT_REQUIRED_MESSAGES,
	getUserValidationSchema,
	isInsightIdRequired,
	needsInsightIds,
} from '../../userValidationSchema';
import { USER_PERMISSION_FIELDS } from '../../UserDrawer/userFormValues';
import { getImportFieldLabel } from './importFields';
import { looksDraggedDown } from './looksDraggedDown';

/** A reviewed row's status: ready, warning (it's still imported, or skipped) or error. */
export const IMPORT_ROW_STATUSES = { ready: 'ready', warning: 'warning', error: 'error' };

const DRAGGED_HINT = '. Excel may have changed the id when the cell was dragged down.';

// Each field's first error from the schema
const getSchemaErrors = (schema, values) => {
	try {
		schema.validateSync(values, { abortEarly: false });
		return {};
	} catch (error) {
		if (!(error instanceof ValidationError)) throw error;
		return Object.fromEntries(
			[...error.inner]
				.reverse()
				.map(({ path, message }) => [path.replace(/\[\d+\]$/, ''), message]),
		);
	}
};

/** A row's status from its errors and warnings (by field). */
export const getImportRowStatus = ({ errors, warnings }) =>
	(Object.keys(errors).length && IMPORT_ROW_STATUSES.error) ||
	(Object.keys(warnings).length && IMPORT_ROW_STATUSES.warning) ||
	IMPORT_ROW_STATUSES.ready;

/** A row's email as it's saved: trimmed and lowercase. */
export const getImportRowEmail = ({ email }) =>
	String(email ?? '')
		.trim()
		.toLowerCase();

/**
 * Checks user import rows in the browser as the admin reviews them, the same way the
 * backend's checkUserImportRows does (with the same messages), against data already loaded.
 * The backend checks them all again when the import starts.
 *
 * Errors (the row can't be imported):
 * - its fields against getUserValidationSchema for a new user: required name, email and
 *   primary broker group; a valid email and ids; other broker groups on the primary's
 *   ledger; a Folio admin needs a Folio email. A permission that isn't Yes / No.
 * - no sales team or authorised rep when needsInsightIds, unless (each) its ledger's Insight
 *   list has loaded with nothing to pick (isInsightIdRequired)
 * - a primary or other broker group that doesn't exist (saying so when the id looks dragged
 *   down in Excel)
 * - an Insight id that isn't in its ledger's Insight list
 * Warnings:
 * - an email already in an earlier row, or already a Folio user's (that row is skipped)
 * - an Insight id that's inactive, or whose list couldn't be read
 *
 * @param {{ rowNumber: number, values: object }[]} rows
 * @param {object} lookups
 * @param {Map<string, object>} lookups.brokerGroupsById every broker group
 * @param {Set<string>} lookups.existingEmails every Folio user's email, lowercase
 * @param {string[]} lookups.clientConfigBrokerGroupIds from getClientConfigBrokerGroupIds
 * @param {object} lookups.insightOptions `${ledger}:${fieldType}` → { options, isError,
 *   isNotAvailable }
 *   (listInsightFieldsForLedgers); a list not in it yet (still loading) isn't checked
 * @returns {{ rowNumber: number, values: object, status: string, errors: object,
 *   warnings: object }[]} errors and warnings by field
 */
export const checkRows = (
	rows,
	{ brokerGroupsById, existingEmails, clientConfigBrokerGroupIds, insightOptions },
) => {
	const schema = getUserValidationSchema(brokerGroupsById, []);
	const fileBrokerGroupIds = rows.flatMap(({ values }) =>
		[values.brokerGroupId, ...(values.brokerGroupArray ?? [])].filter(Boolean),
	);
	const missingMessage = (ids) =>
		// Worded as the backend's getBrokerGroupNotFoundMessage (services/validation/user.js)
		`No broker group${ids.length > 1 ? 's' : ''} with id${ids.length > 1 ? 's' : ''} ${ids.join(', ')}${
			ids.some((id) => looksDraggedDown(id, fileBrokerGroupIds)) ? DRAGGED_HINT : ''
		}`;
	const rowsByEmail = new Map();

	return rows.map(({ rowNumber, values }) => {
		const errors = getSchemaErrors(schema, values);
		const warnings = {};

		USER_PERMISSION_FIELDS.forEach((field) => {
			if (typeof values[field] !== 'boolean') {
				errors[field] = `${getImportFieldLabel(field)} must be Yes or No`;
			}
		});

		const primary = brokerGroupsById.get(values.brokerGroupId);
		if (values.brokerGroupId && !errors.brokerGroupId && !primary) {
			errors.brokerGroupId = missingMessage([values.brokerGroupId]);
		}
		const missingOthers = (values.brokerGroupArray ?? []).filter(
			(id) => !brokerGroupsById.has(id),
		);
		if (!errors.brokerGroupArray && missingOthers.length) {
			errors.brokerGroupArray = missingMessage(missingOthers);
		}

		if (primary && needsInsightIds(values, clientConfigBrokerGroupIds)) {
			Object.entries(INSIGHT_REQUIRED_MESSAGES).forEach(([field, message]) => {
				const list = insightOptions[`${primary.ledger}:${USER_INSIGHT_FIELD_TYPES[field]}`];
				if (values[field] == null && !errors[field] && isInsightIdRequired(list)) {
					errors[field] = message;
				}
			});
		}

		if (primary?.ledger) {
			Object.entries(USER_INSIGHT_FIELD_TYPES).forEach(([field, fieldType]) => {
				const list = insightOptions[`${primary.ledger}:${fieldType}`];
				if (values[field] == null || errors[field] || !list) return;
				if (list.isError) {
					warnings[field] = list.isNotAvailable
						? `Couldn't check this id: this ledger's Insight doesn't offer ${fieldType}`
						: `Couldn't check this id: ${fieldType} couldn't be read from Insight`;
					return;
				}
				const option = list.options.find(({ id }) => id === Number(values[field]));
				if (!option) {
					errors[field] =
						`${values[field]} isn't a ${fieldType} id in Insight for ${primary.ledger}`;
				} else if (option.isInactive) {
					warnings[field] = `${option.name} is inactive in Insight`;
				}
			});
		}

		const email = getImportRowEmail(values);
		if (email && !errors.email) {
			const earlierRow = rowsByEmail.get(email);
			if (earlierRow) {
				warnings.email = `${email} is already in row ${earlierRow} of this file`;
			} else {
				rowsByEmail.set(email, rowNumber);
				if (existingEmails.has(email)) {
					warnings.email = `${email} is already a Folio user's email`;
				}
			}
		}

		return {
			rowNumber,
			values,
			errors,
			warnings,
			status: getImportRowStatus({ errors, warnings }),
		};
	});
};
